import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  FormArray,
  FormControl,
  Validators
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

import { ProductService } from 'app/services/product/product.service';
import { LookupCacheService } from 'app/services/lookup-cache/lookup-cache.service';

import {
  IDropdownSettings,
  NgMultiSelectDropDownModule
} from 'ng-multiselect-dropdown';

import {
  FullProductDtoLight,
  LightStockDto
} from 'app/models/product-data-dto';

import { ImageItem } from 'app/services/gallery/gallery.service';
import { GalleryPickerComponent } from 'app/shared/gallery-picker/gallery-picker.component';
import { ImageUrlPipe } from 'app/modules/admin/pipe/image-url.pipe';
import { ImageFallbackDirective } from 'app/modules/admin/directives/ImageFallbackDirective';

type LookupOption = {
  id: number | string;
  name: string;
};

@Component({
  selector: 'app-edit-product',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NgMultiSelectDropDownModule,
    GalleryPickerComponent,
    ImageUrlPipe,
    ImageFallbackDirective
  ],
  templateUrl: './edit-product.component.html',
  styleUrls: ['./edit-product.component.css']
})
export class EditProductComponent implements OnInit {
  productForm!: FormGroup;

  activeTab = 'basic';
  submitted = false;
  isLoading = false;
  isSaving = false;

  currentProductId = 0;
  productSlug = '';

  errorMessage = '';

  isGalleryModalOpen = false;
  selectedPhotos: ImageItem[] = [];
  selectedPhotoIds: number[] = [];

  tabs = [
    { key: 'basic', labelAr: 'البيانات الأساسية', labelEn: 'Basic Info' },
    { key: 'price', labelAr: 'السعر والمخزون', labelEn: 'Price & Stock' },
    { key: 'media', labelAr: 'الوسائط والتهيئة', labelEn: 'Media & Settings' }
  ];

  sources: LookupOption[] = [
    { id: 'main', name: 'رئيسي' },
    { id: 'sub', name: 'فرعي' }
  ];

  mainCategories: LookupOption[] = [];
  subCategories: LookupOption[] = [];
  brands: LookupOption[] = [];
  units: LookupOption[] = [];
  variants: LookupOption[] = [];
  stores: LookupOption[] = [];

  multiDropdownSettings: IDropdownSettings = {
    singleSelection: false,
    idField: 'id',
    textField: 'name',
    selectAllText: 'تحديد الكل',
    unSelectAllText: 'إلغاء الكل',
    allowSearchFilter: true,
    searchPlaceholderText: 'بحث...',
    itemsShowLimit: 4,
    closeDropDownOnSelection: false
  };

  singleDropdownSettings: IDropdownSettings = {
    singleSelection: true,
    idField: 'id',
    textField: 'name',
    allowSearchFilter: true,
    searchPlaceholderText: 'بحث...',
    itemsShowLimit: 1,
    closeDropDownOnSelection: true
  };

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private productService: ProductService,
    private lookupCacheService: LookupCacheService
  ) { }

  async ngOnInit(): Promise<void> {
    this.createForm();

    this.productSlug = this.route.snapshot.paramMap.get('slug') || '';

    this.currentProductId = Number(this.productSlug.split('-')[0]);

    await this.loadPageData();

    if (this.productSlug) {
      await this.loadProductForEdit(this.currentProductId);
    }
  }

  private createForm(): void {
    this.productForm = this.fb.group({
      sourceType: ['sub', Validators.required],

      mainCategoryId: [[], Validators.required],
      subCategoryIds: [[]],
      brandId: [[], Validators.required],

      parentId: [],
      status: [true],
      numOfSale: [0, [Validators.min(0)]],

      branchId: [1, Validators.required],
      storeId: [1, Validators.required],

      arName: ['', [Validators.required, Validators.minLength(2)]],
      enName: ['', [Validators.required, Validators.minLength(2)]],
      arDescription: [''],
      enDescription: [''],

      purchasePrice: [0, [Validators.required, Validators.min(0)]],
      sellingPrice: [0, [Validators.required, Validators.min(0)]],
      sku: ['', Validators.required],
      barcode: ['', Validators.required],

      stocks: this.fb.array([]),

      published: [true],
      approved: [false],
      isFeatured: [false],
      isTodaysDeal: [false],
      isBestSeller: [false],

      discount: [
        0,
        [
          Validators.min(0),
          Validators.max(100)
        ]
      ],

      maxPurchaseQty: [0, [Validators.required, Validators.min(0)]],

      variantId: [null],
      video_link: [null],
      unitId: [null, Validators.required],
      unitFactor: ['1', Validators.required],
      barcodeType: ['0', Validators.required],

      weightKg: [0, [Validators.min(0)]],
      minPurchaseQty: [1, [Validators.required, Validators.min(1)]],

      slug: [
        '',
        [
          Validators.required,
          Validators.pattern(/^[a-zA-Z0-9]+(?:-+[a-zA-Z0-9]+)*$/)
        ]
      ],

      photosIdsText: ['']
    });

    this.addStock();

    this.productForm.get('sourceType')?.valueChanges.subscribe(value => {
      if (value === 'main') {
        this.productForm.patchValue({ subCategoryIds: [] });
      }
    });
  }

  private async loadPageData(): Promise<void> {
    this.isLoading = true;
    this.errorMessage = '';

    try {
      const lookups = await firstValueFrom(
        this.lookupCacheService.getLookups()
      );

      this.mainCategories = lookups.mainCategories || [];
      this.subCategories = lookups.subCategories || [];
      this.brands = lookups.brands || [];
      this.units = lookups.units || [];
      this.variants = lookups.variants || [];
      this.stores = lookups.stores || [];
    } catch (error) {
      console.error('Lookup loading error:', error);
      this.errorMessage = 'حدث خطأ أثناء تحميل بيانات الاختيارات.';
    } finally {
      this.isLoading = false;
    }
  }

  async refreshLookups(): Promise<void> {
    this.isLoading = true;
    this.errorMessage = '';

    try {
      const lookups = await firstValueFrom(
        this.lookupCacheService.refreshLookups()
      );

      this.mainCategories = lookups.mainCategories || [];
      this.subCategories = lookups.subCategories || [];
      this.brands = lookups.brands || [];
      this.units = lookups.units || [];
      this.variants = lookups.variants || [];
      this.stores = lookups.stores || [];

      alert('✅ تم تحديث بيانات الاختيارات بنجاح');
    } catch (error) {
      console.error('Refresh lookups error:', error);
      alert('❌ حدث خطأ أثناء تحديث بيانات الاختيارات');
    } finally {
      this.isLoading = false;
    }
  }

  private async loadProductForEdit(id: number): Promise<void> {
    this.isLoading = true;
    this.errorMessage = '';

    try {
      const res: any = await firstValueFrom(
        this.productService.getProductById(id)
      );

      console.log(res);

      const product = res?.data?.[0];

      if (!res?.success || !product) {
        this.errorMessage = res?.message || 'المنتج غير موجود.';
        return;
      }

      this.currentProductId = Number(product.id);

      this.patchProduct(product);
    } catch (error) {
      console.error('Product loading error:', error);
      this.errorMessage = 'حدث خطأ أثناء تحميل بيانات المنتج.';
    } finally {
      this.isLoading = false;
    }
  }

  private patchProduct(product: FullProductDtoLight): void {
    console.log(product);

    this.patchStocks(product);

    const mainCategoryId = product.main_category_id ?? null;
    const brandId = product.brand_id ?? null;
    const variantId = product.variant_id ?? null;
    const unitId = product.unit_id ?? null;

    const subIds = this.extractSubCategoryIds(product);
    const photoIds = this.extractPhotoIds(product);

    this.selectedPhotoIds = photoIds;

    this.selectedPhotos = (product.photos_urls || []).map((url: string, index: number) => ({
      id: photoIds[index] ?? 0,
      name: url.split('/').pop() || 'image',
      url: url.startsWith('/') ? url : '/' + url,
      path: url,
      size: 0,
      date: ''
    } as ImageItem));

    const mainCategorySelection = this.optionArrayById(
      this.mainCategories,
      mainCategoryId
    );

    const brandSelection = this.optionArrayById(
      this.brands,
      brandId
    );

    const subCategorySelection = this.optionsArrayByIds(
      this.subCategories,
      subIds
    );

    this.productForm.patchValue({
      sourceType: product.source_type ?? 'sub',

      mainCategoryId: mainCategorySelection,
      subCategoryIds: subCategorySelection,
      brandId: brandSelection,

      parentId: product.parent_id,

      status: this.toBool(product.status, true),
      numOfSale: Number(product.num_of_sale ?? 0),

      isBestSeller: this.toBool(product.is_best_seller, false),
      discount: Number(product.discount ?? 0),
      maxPurchaseQty: Number(product.max_purchase_qty ?? 0),

      storeId: Number(product.store_id ?? 1),
      video_link: product.video_link ?? "",

      arName: product.ar_name ?? '',
      enName: product.en_name ?? '',

      arDescription: product.ar_description ?? '',
      enDescription: product.en_description ?? '',

      purchasePrice: Number(product.purchase_price ?? 0),
      sellingPrice: Number(product.selling_price ?? 0),

      sku: product.sku ?? '',
      barcode: product.barcode ?? '',

      published: this.toBool(product.published, true),
      approved: this.toBool(product.approved, false),
      isFeatured: this.toBool(product.is_featured, false),
      isTodaysDeal: this.toBool(product.is_todays_deal, false),

      variantId: variantId && Number(variantId) > 0 ? Number(variantId) : null,
      unitId: unitId ? unitId.toString() : null,

      unitFactor: (product.unit_factor ?? '1').toString(),
      barcodeType: (product.barcode_type ?? '0').toString(),

      weightKg: Number(product.weight_kg ?? 0),
      minPurchaseQty: Math.max(Number(product.min_purchase_qty ?? 1), 1),
      slug: product.slug ?? '',

      photosIdsText: photoIds.join(',')
    });

    console.log('✅ Product patched:', this.productForm.value);
  }

  private patchStocks(product: FullProductDtoLight): void {
    const sourceStocks = product.stocks ?? [];

    this.stocks.clear();

    if (!Array.isArray(sourceStocks) || sourceStocks.length === 0) {
      this.addStock({
        store_id: Number(product.store_id ?? 1),
        quantity: 0,
        price: Number(product.selling_price ?? 0)
      });

      return;
    }
    console.log(sourceStocks);
    sourceStocks.forEach((item: LightStockDto) => {
      this.addStock({
        store_id: Number(item.store_id ?? 1),
        quantity: Number(item.quantity ?? 0),
        price: Number(item.price ?? product.selling_price ?? 0)
      });
    });
  }

  get stocks(): FormArray {
    return this.productForm.get('stocks') as FormArray;
  }

  get mainCategoryControl(): FormControl {
    return this.productForm.get('mainCategoryId') as FormControl;
  }

  get subCategoryControl(): FormControl {
    return this.productForm.get('subCategoryIds') as FormControl;
  }

  get brandControl(): FormControl {
    return this.productForm.get('brandId') as FormControl;
  }

  private createStockGroup(data?: LightStockDto): FormGroup {
    return this.fb.group({
      storeId: [Number(data?.store_id ?? 1), Validators.required],
      quantity: [Number(data?.quantity ?? 0), [Validators.min(0)]],
      price: [Number(data?.price ?? 0), [Validators.min(0)]]
    });
  }

  addStock(data?: LightStockDto): void {
    this.stocks.push(this.createStockGroup(data));
  }

  removeStock(index: number): void {
    if (this.stocks.length <= 1) return;
    this.stocks.removeAt(index);
  }

  setActiveTab(tabKey: string): void {
    this.activeTab = tabKey;
  }

  nextTab(): void {
    const currentIndex = this.tabs.findIndex(tab => tab.key === this.activeTab);

    if (currentIndex < this.tabs.length - 1) {
      this.activeTab = this.tabs[currentIndex + 1].key;
    }
  }

  previousTab(): void {
    const currentIndex = this.tabs.findIndex(tab => tab.key === this.activeTab);

    if (currentIndex > 0) {
      this.activeTab = this.tabs[currentIndex - 1].key;
    }
  }

  isFirstTab(): boolean {
    return this.activeTab === this.tabs[0].key;
  }

  isLastTab(): boolean {
    return this.activeTab === this.tabs[this.tabs.length - 1].key;
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.productForm.get(fieldName);

    return !!(
      field &&
      field.invalid &&
      (field.touched || field.dirty || this.submitted)
    );
  }

  isDropdownInvalid(fieldName: string): boolean {
    const value = this.productForm.get(fieldName)?.value;

    return !!(
      this.submitted &&
      (!Array.isArray(value) || value.length === 0)
    );
  }

  getFieldError(fieldName: string): string {
    const field = this.productForm.get(fieldName);

    if (!field || !field.errors) return '';

    if (field.errors['required']) return 'هذا الحقل مطلوب';

    if (field.errors['min']) {
      return `القيمة يجب ألا تقل عن ${field.errors['min'].min}`;
    }

    if (field.errors['max']) {
      return `القيمة يجب ألا تزيد عن ${field.errors['max'].max}`;
    }

    if (field.errors['minlength']) {
      return `الحد الأدنى للحروف هو ${field.errors['minlength'].requiredLength}`;
    }

    return 'قيمة غير صحيحة';
  }

  onSubmit(): void {
    this.submitted = true;
    this.markFormGroupTouched(this.productForm);

    if (!this.validateFormBeforeSubmit()) {
      return;
    }

    const dto = this.buildUpdateDto();

    console.log('Update DTO:', dto);

    this.updateProduct(dto);
  }

  private logFormErrors(): void {
    Object.keys(this.productForm.controls).forEach(key => {
      const control = this.productForm.get(key);

      if (control?.invalid) {
        console.log(key, control.errors, control.value);
      }
    });
  }

  private getFormErrors(): string[] {
    const formErrors: string[] = [];

    Object.keys(this.productForm.controls).forEach(key => {
      const control = this.productForm.get(key);

      if (control?.invalid) {
        if (control.errors?.['required']) {
          formErrors.push(`${key} مطلوب`);
        }
        if (control.errors?.['pattern']) {
          formErrors.push(`${key} الصيغة غير صحيحة`);
        }

        if (control.errors?.['min']) {
          formErrors.push(`${key} أقل من القيمة المسموحة`);
        }

        if (control.errors?.['max']) {
          formErrors.push(`${key} أكبر من القيمة المسموحة`);
        }

        if (control.errors?.['minlength']) {
          formErrors.push(`${key} عدد الحروف أقل من المطلوب`);
        }

        if (control.errors?.['maxlength']) {
          formErrors.push(`${key} عدد الحروف أكبر من المطلوب`);
        }
      }
    });

    return formErrors;
  }

  private validateFormBeforeSubmit(): boolean {
    const errors: string[] = [];
    this.logFormErrors();
    if (this.productForm.invalid) {
      errors.push('يوجد حقول مطلوبة أو قيم غير صحيحة.');
      errors.push(...this.getFormErrors());
    }

    const mainCategoryId = this.getSingleDropdownId(
      this.productForm.value.mainCategoryId
    );

    if (!mainCategoryId) {
      errors.push('الفئة الرئيسية مطلوبة.');
    }

    const brandId = this.getSingleDropdownId(
      this.productForm.value.brandId
    );

    if (!brandId) {
      errors.push('البراند مطلوب.');
    }

    if (this.stocks.length === 0) {
      errors.push('يجب إضافة مخزن واحد على الأقل.');
    }

    const storeIds = this.stocks.value
      .map((x: any) => Number(x.storeId))
      .filter((x: number) => Number.isFinite(x));

    const hasDuplicateStores = storeIds.some(
      (id: number, index: number) => storeIds.indexOf(id) !== index
    );

    if (hasDuplicateStores) {
      errors.push('لا يمكن تكرار نفس المتجر داخل المخزون.');
    }

    if (errors.length > 0) {
      alert('❌ ' + errors.join('\n'));
      return false;
    }

    return true;
  }

  private buildUpdateDto(): any {
    const v = this.productForm.value;

    const mainCategoryId = this.getSingleDropdownId(v.mainCategoryId);
    const brandId = this.getSingleDropdownId(v.brandId);
    const subCategoryIds = this.getMultiDropdownIds(v.subCategoryIds);

    const stocks = (v.stocks || []).map((item: any) => ({
      storeId: item.storeId?.toString(),
      quantity: Number(item.quantity ?? 0),
      price: Number(item.price ?? 0)
    }));

    return {
      sourceType: v.sourceType,

      mainCategoryId: mainCategoryId?.toString() ?? null,
      subCategoryIds,

      parentId: v.parentId ? Number(v.parentId) : null,
      brandId: brandId ? Number(brandId) : null,

      status: !!v.status,
      numOfSale: Number(v.numOfSale ?? 0),

      storeId: v.storeId ? Number(v.storeId) : null,

      arName: v.arName ?? '',
      enName: v.enName ?? '',

      arDescription: v.arDescription ?? '',
      enDescription: v.enDescription ?? '',

      purchasePrice: Number(v.purchasePrice ?? 0),
      sellingPrice: Number(v.sellingPrice ?? 0),

      sku: v.sku ?? '',
      barcode: v.barcode ?? '',

      stocks,

      discount: Number(v.discount ?? 0),
      maxPurchaseQty: Number(v.maxPurchaseQty ?? 1),
      isBestSeller: !!v.isBestSeller,

      published: !!v.published,
      approved: !!v.approved,
      isFeatured: !!v.isFeatured,
      isTodaysDeal: !!v.isTodaysDeal,

      variantId: v.variantId ? Number(v.variantId) : null,
      video_link: v.video_link ?? "",

      unitId: v.unitId?.toString() ?? null,
      unitFactor: v.unitFactor?.toString() ?? '1',
      barcodeType: v.barcodeType?.toString() ?? '0',

      weightKg: Number(v.weightKg ?? 0),
      minPurchaseQty: Math.max(Number(v.min_purchase_qty ?? 1), 1),
      slug: v.slug ?? '',

      photos_ids: this.parseIdsText(v.photosIdsText)
    };
  }

  private updateProduct(dto: any): void {
    if (!this.currentProductId) {
      alert('❌ لا يوجد Product ID للتحديث.');
      return;
    }

    this.isSaving = true;

    this.productService.updateProduct(this.currentProductId, dto).subscribe({
      next: (response: any) => {
        console.log('Update response:', response);
        alert('✅ تم تحديث المنتج بنجاح');
        this.router.navigate(['/admin/products']);
      },
      error: (error: HttpErrorResponse) => {
        console.error('Update error:', error);
        this.handleError(error);
        this.isSaving = false;
      },
      complete: () => {
        this.isSaving = false;
      }
    });
  }

  onReset(): void {
    if (!this.productSlug) return;

    if (confirm('هل تريد إعادة تحميل بيانات المنتج من جديد؟')) {
      this.loadProductForEdit(this.currentProductId);
    }
  }

  onCancel(): void {
    if (confirm('هل تريد إلغاء التعديلات؟')) {
      this.router.navigate(['/admin/products']);
    }
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      } else if (control instanceof FormArray) {
        control.controls.forEach(arrayControl => {
          if (arrayControl instanceof FormGroup) {
            this.markFormGroupTouched(arrayControl);
          } else {
            arrayControl.markAsTouched();
          }
        });
      } else {
        control?.markAsTouched();
      }
    });
  }

  private handleError(error: HttpErrorResponse): void {
    if (error.error?.errors) {
      console.error('Validation errors:', error.error.errors);
      alert('❌ يوجد أخطاء في البيانات. راجع Console.');
      return;
    }

    if (error.error?.message) {
      alert('❌ ' + error.error.message);
      return;
    }

    alert('❌ حدث خطأ أثناء تحديث المنتج.');
  }

  private optionArrayById(list: LookupOption[], id: any): LookupOption[] {
    if (id === null || id === undefined || id === '') return [];

    const found = list.find(item => Number(item.id) === Number(id));

    if (found) return [found];

    return [
      {
        id: Number(id),
        name: `#${id}`
      }
    ];
  }

  private optionsArrayByIds(list: LookupOption[], ids: number[]): LookupOption[] {
    return ids
      .filter(id => Number.isFinite(Number(id)) && Number(id) > 0)
      .map(id => {
        const found = list.find(item => Number(item.id) === Number(id));

        return found || {
          id,
          name: `#${id}`
        };
      });
  }

  private getSingleDropdownId(value: any): number | null {
    if (Array.isArray(value)) {
      const first = value[0];

      if (!first) return null;

      return Number(first.id ?? first);
    }

    if (value && typeof value === 'object') {
      return Number(value.id);
    }

    if (value === null || value === undefined || value === '') {
      return null;
    }

    return Number(value);
  }

  private getMultiDropdownIds(value: any): number[] {
    if (!Array.isArray(value)) return [];

    return value
      .map(item => Number(item?.id ?? item))
      .filter(id => Number.isFinite(id) && id > 0);
  }

  private extractSubCategoryIds(product: FullProductDtoLight): number[] {
    const raw = product.sub_category_ids ?? [];

    if (!Array.isArray(raw)) return [];

    return raw
      .map(id => Number(id))
      .filter(id => Number.isFinite(id) && id > 0);
  }

  private extractPhotoIds(product: FullProductDtoLight): number[] {
    const raw = product.photos_ids ?? [];

    if (Array.isArray(raw)) {
      return raw
        .map(id => Number(id))
        .filter(id => Number.isFinite(id) && id > 0);
    }

    // if (typeof raw === 'string') {
    //   return raw
    //     .split(',')
    //     .map(x => Number(x.trim()))
    //     .filter(id => Number.isFinite(id) && id > 0);
    // }

    return [];
  }

  private parseIdsText(value: string): number[] {
    if (!value) return [];

    return value
      .split(',')
      .map(x => Number(x.trim()))
      .filter(id => Number.isFinite(id) && id > 0);
  }

  private toBool(value: any, fallback: boolean): boolean {
    if (value === true || value === 1 || value === '1' || value === 'true') {
      return true;
    }

    if (value === false || value === 0 || value === '0' || value === 'false') {
      return false;
    }

    return fallback;
  }

  openGalleryModal(): void {
    this.isGalleryModalOpen = true;
  }

  closeGalleryModal(): void {
    this.isGalleryModalOpen = false;
  }

  onGalleryImagesChange(images: ImageItem[]): void {
    this.selectedPhotos = images || [];

    this.selectedPhotoIds = this.selectedPhotos
      .map(x => Number(x.id))
      .filter(id => Number.isFinite(id) && id > 0);

    this.patchPhotosIdsToForm();
  }

  removeSelectedPhoto(photo: ImageItem): void {
    this.selectedPhotos = this.selectedPhotos.filter(x => Number(x.id) !== Number(photo.id));

    this.selectedPhotoIds = this.selectedPhotos
      .map(x => Number(x.id))
      .filter(id => Number.isFinite(id) && id > 0);

    this.patchPhotosIdsToForm();
  }

  isGalleryImageSelected(image: ImageItem): boolean {
    return this.selectedPhotoIds.includes(Number(image.id));
  }

  private patchPhotosIdsToForm(): void {
    this.productForm.patchValue({
      photosIdsText: this.selectedPhotoIds.join(',')
    });

    this.productForm.get('photosIdsText')?.markAsDirty();
    this.productForm.get('photosIdsText')?.updateValueAndValidity();
  }
}